import { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { Href } from 'expo-router';
import { Link, useSitemap } from 'expo-router';
import { BodyText, Container, LabelText, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const ExpoRouterDemoSitemap = () => {
  const sitemap = useSitemap();

  const routes = getSitemapRoutes(sitemap?.children);

  return (
    <ScrollView style={styles.scroll}>
      <SafeAreaView>
        <Container style={styles.contentContainer}>
          {routes.map((node) => (
            <ExpoRouterDemoSitemapGroup key={node.contextKey} node={node} />
          ))}
        </Container>
      </SafeAreaView>
    </ScrollView>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ExpoRouterDemoSitemapGroup = (params: GroupProps) => {
  const { node, depth = 0 } = params;

  const sitemapNodes = [...node.children].sort(sortByGroupsFirst);

  // ─── State ───────────────────────────────────────────────────────────

  const [isExpanded, setIsExpanded] = useState(false);

  // ─────────────────────────────────────────────────────────────────────

  if (isGroupNode(node)) {
    return (
      <View style={styles.group}>
        <Pressable onPress={() => setIsExpanded((prev) => !prev)}>
          <LabelText sizeMatch={['12']} style={styles.groupLabel}>
            {node.filename} {isExpanded ? '▲' : '▼'}
          </LabelText>
        </Pressable>

        {isExpanded ? (
          <View style={styles.groupChildren}>
            {sitemapNodes.map((child) => (
              <ExpoRouterDemoSitemapGroup
                key={child.contextKey}
                node={child}
                depth={depth + 1}
              />
            ))}
          </View>
        ) : null}
      </View>
    );
  }

  return <ExpoRouterDemoSitemapLink node={node} />;
};

const ExpoRouterDemoSitemapLink = (params: LinkProps) => {
  const { node } = params;

  return (
    <Link href={node.href} style={styles.link}>
      <BodyText sizeMatch={['14']} style={styles.linkText}>
        {node.filename}
      </BodyText>

      <LabelText sizeMatch={['12']} style={styles.linkHref}>
        {node.href as string}
      </LabelText>
    </Link>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSitemapRoutes(nodes: SitemapNode[] | undefined) {
  return (nodes ?? [])
    .filter((node) => !node.isGenerated)
    .sort(sortByGroupsFirst);
}

function isGroupNode(node: SitemapNode) {
  return node.children.length > 0 || node.contextKey.includes('_layout');
}

function sortByGroupsFirst(a: SitemapNode, b: SitemapNode) {
  const aIsGroup = isGroupNode(a);
  const bIsGroup = isGroupNode(b);

  if (aIsGroup === bIsGroup) return 0;

  return aIsGroup ? -1 : 1;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    width: '100%',
  },
  contentContainer: {
    paddingTop: theme.spacing['12'],
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['6'],
    gap: theme.spacing['2'],
  },
  group: {
    borderWidth: 1,
    borderColor: theme.colors.OPACITY.DARK_KALE.LIGHT,
    borderRadius: theme.radius.medium,
    overflow: 'hidden',
  },
  groupLabel: {
    backgroundColor: theme.colors.KALE,
    color: theme.colors.WHITE,
    paddingHorizontal: theme.spacing['3'],
    paddingVertical: theme.spacing['2'],
  },
  groupChildren: {
    padding: theme.spacing['2'],
    gap: theme.spacing['2'],
  },
  link: {
    backgroundColor: theme.colors.WHITE,
    borderRadius: theme.radius.small,
    paddingHorizontal: theme.spacing['3'],
    paddingVertical: theme.spacing['2'],
  },
  linkText: {
    color: theme.colors.KALE,
  },
  linkHref: {
    color: theme.colors.CHARCOAL,
    opacity: 0.6,
  },
});

// ─── Types ────────────────────────────────────────────────────────────────────

type SitemapNode = {
  contextKey: string;
  filename: string;
  href: string | Href;
  isInitial: boolean;
  isInternal: boolean;
  isGenerated: boolean;
  children: SitemapNode[];
};

type GroupProps = {
  node: SitemapNode;
  depth?: number;
};

type LinkProps = {
  node: SitemapNode;
};
